/**
 * Client-side URL preview utility.
 *
 * Fetches Open Graph metadata (title, description, thumbnail) for a given URL
 * entirely from the client, without any server-side proxying:
 *
 * - On native Capacitor (Android/iOS): uses `CapacitorHttp.get()` which makes
 *   the request through the device's native HTTP stack and therefore bypasses
 *   browser CORS restrictions.
 * - On web: falls back to a regular `fetch()` call.  Most cross-origin pages
 *   will be blocked by CORS and the call will fail silently, returning `null`.
 *   Same-origin or CORS-enabled URLs will work normally.
 *
 * All errors (CORS, network, timeout, missing data) result in `null` being
 * returned – the caller should treat a `null` result as "preview unavailable".
 */

export interface UrlPreview {
  title?: string;
  description?: string;
  /** og:image URL (resolved to absolute) */
  thumbnail?: string;
  /** The final URL after following redirects (if any) */
  finalUrl?: string;
}

const MAX_REDIRECT_HOPS = 5;
const PREVIEW_FETCH_TIMEOUT_MS = 5_000;

/**
 * Check if redirecting from `fromUrl` to `toUrl` is safe.
 * Prevents HTTPS downgrade and ensures the new URL is valid HTTP(S).
 */
function isRedirectSafe(fromUrl: URL, toUrl: string): boolean {
  try {
    const to = new URL(toUrl);
    // Only allow HTTP(S) protocols
    if (to.protocol !== 'http:' && to.protocol !== 'https:') {
      return false;
    }
    // Prevent HTTPS downgrade
    if (fromUrl.protocol === 'https:' && to.protocol === 'http:') {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Return true when `url` is an absolute http(s) URL.
 */
function isHttpUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Parse OG / standard meta tags from an HTML string.
 * Thumbnail URLs (og:image, twitter:image) are resolved to absolute URLs
 * using `baseUrl` so relative paths work correctly as `<img src>`.
 *
 * Exported for unit testing.
 */
export function parseOgTags(html: string, baseUrl: string): UrlPreview {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Iterate meta elements rather than using a CSS attribute-value selector to
  // avoid any risk of special characters in `value` being mis-interpreted.
  const getMeta = (
    attr: 'property' | 'name',
    value: string
  ): string | undefined => {
    for (const el of doc.querySelectorAll<HTMLMetaElement>('meta')) {
      if (el.getAttribute(attr) === value) {
        return el.content || undefined;
      }
    }
    return undefined;
  };

  const title =
    getMeta('property', 'og:title') ??
    getMeta('name', 'twitter:title') ??
    (doc.title || undefined);

  const description =
    getMeta('property', 'og:description') ??
    getMeta('name', 'twitter:description') ??
    getMeta('name', 'description');

  const rawThumbnail =
    getMeta('property', 'og:image') ?? getMeta('name', 'twitter:image');

  // Resolve relative thumbnail paths against the fetched URL so the resulting
  // value is always usable as an <img src> attribute on the current page.
  let thumbnail: string | undefined;
  if (rawThumbnail) {
    try {
      thumbnail = new URL(rawThumbnail, baseUrl).href;
    } catch {
      thumbnail = rawThumbnail;
    }
  }

  return { title, description, thumbnail };
}

/**
 * Fetch URL preview metadata for the given URL entirely from the client.
 *
 * Safely follows HTTP redirects (up to 5 hops) with these protections:
 * - Limits redirect count to prevent infinite loops
 * - Prevents HTTPS → HTTP downgrade attacks
 * - Only allows HTTP(S) protocols
 *
 * @returns `UrlPreview` when at least a title was found, `null` otherwise.
 */
export async function fetchUrlPreview(url: string): Promise<UrlPreview | null> {
  if (!isHttpUrl(url)) return null;

  try {
    const { Capacitor } = await import('@capacitor/core');
    const isNative = Capacitor.isNativePlatform();

    // Track redirect chain for loop detection
    const seenUrls = new Set<string>([url]);
    let currentUrl = url;
    let html: string | undefined;
    let finalUrl = url;

    // Manually follow redirects with safety checks
    for (let hop = 0; hop < MAX_REDIRECT_HOPS && !html; hop++) {
      const urlObj = new URL(currentUrl);

      if (isNative) {
        // Native: CapacitorHttp bypasses CORS – request goes through the device's
        // own HTTP stack (Java HttpURLConnection on Android, URLSession on iOS).
        // Note: CapacitorHttp follows redirects automatically, but we disable it
        // to implement our own safety checks.
        const { CapacitorHttp } = await import('@capacitor/core');
        const response = await CapacitorHttp.get({
          url: currentUrl,
          headers: { Accept: 'text/html,application/xhtml+xml' },
          responseType: 'text',
          readTimeout: PREVIEW_FETCH_TIMEOUT_MS,
          connectTimeout: PREVIEW_FETCH_TIMEOUT_MS,
          // Disable auto-redirect to handle manually with safety checks
          disableRedirects: true,
        });

        if (response.status >= 300 && response.status < 400) {
          const location =
            (response.headers as Record<string, string>)['location'] ??
            (response.headers as Record<string, string>)['Location'];
          if (!location) return null;

          if (!isRedirectSafe(urlObj, location)) return null;
          currentUrl = location;
          finalUrl = currentUrl;

          // Check for redirect loops
          if (seenUrls.has(currentUrl)) return null;
          seenUrls.add(currentUrl);
          continue;
        }

        if (response.status < 200 || response.status >= 300) return null;
        const contentType =
          (response.headers as Record<string, string>)['content-type'] ??
          (response.headers as Record<string, string>)['Content-Type'] ??
          '';
        if (!contentType.includes('html')) return null;
        html =
          typeof response.data === 'string'
            ? response.data
            : String(response.data);
      } else {
        // Web: use manual redirect: 'manual' mode to handle redirects ourselves.
        // eslint-disable-next-line warp-drive/no-external-request-patterns -- user-initiated URL preview, not an API call
        const response = await fetch(currentUrl, {
          headers: { Accept: 'text/html,application/xhtml+xml' },
          signal: AbortSignal.timeout(PREVIEW_FETCH_TIMEOUT_MS),
          redirect: 'manual',
        });

        if (response.status >= 300 && response.status < 400) {
          const location = response.headers.get('Location');
          if (!location) return null;

          if (!isRedirectSafe(urlObj, location)) return null;
          // Resolve relative Location headers against current URL
          currentUrl = new URL(location, currentUrl).href;
          finalUrl = currentUrl;

          // Check for redirect loops
          if (seenUrls.has(currentUrl)) return null;
          seenUrls.add(currentUrl);
          continue;
        }

        if (!response.ok) return null;
        const contentType = response.headers.get('Content-Type') ?? '';
        if (!contentType.includes('html')) return null;
        html = await response.text();
      }
    }

    // If we exhausted hops without getting HTML, fail
    if (!html) return null;

    const preview = parseOgTags(html, finalUrl);
    // Only return a preview when we have at least a title to display.
    if (!preview.title) return null;

    return { ...preview, finalUrl };
  } catch {
    // Network error, CORS block, timeout, AbortError, import failure, etc.
    return null;
  }
}
